import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Shield, 
  Copy,
  Check,
  Loader2,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';
import QRCode from 'react-qr-code';

export default function MFASetupPage() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: intro, 2: scan, 3: verify, 4: backup
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);

  const initiateSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/auth/mfa/setup/initiate');
      setSetupData(response.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/mfa/setup/verify', {
        secret_key: setupData.secret_key,
        verification_code: verificationCode,
      });
      setBackupCodes(response.data.backup_codes);
      setStep(4);
      toast.success('MFA enabled successfully!');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(setupData.secret_key);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
    toast.success('Secret key copied!');
  };

  const copyBackupCodes = async () => {
    const text = backupCodes.join('\n');
    await navigator.clipboard.writeText(text);
    setCopiedBackup(true);
    setTimeout(() => setCopiedBackup(false), 2000);
    toast.success('Backup codes copied!');
  };

  if (user?.mfa_enabled) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto text-center py-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#10B981]/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold text-[#E6F1FF] mb-2">MFA Already Enabled</h1>
          <p className="text-[#8892B0] mb-6">
            Two-factor authentication is already enabled on your account.
          </p>
          <Button
            onClick={() => navigate('/settings')}
            className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D]"
          >
            Back to Settings
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="mfa-setup-page">
        {/* Progress */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center font-bold
                ${step >= s ? 'bg-[#2BBBAD] text-[#0B1C2D]' : 'bg-[#1A2F4B] text-[#8892B0]'}
              `}>
                {step > s ? <Check size={20} /> : s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 mx-2 rounded ${step > s ? 'bg-[#2BBBAD]' : 'bg-[#1A2F4B]'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Introduction */}
        {step === 1 && (
          <div className="glass-card rounded-2xl p-8 text-center animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#2BBBAD]/20 flex items-center justify-center">
              <Shield className="w-10 h-10 text-[#2BBBAD]" />
            </div>
            <h1 className="text-2xl font-bold text-[#E6F1FF] mb-2">
              Enable Two-Factor Authentication
            </h1>
            <p className="text-[#8892B0] mb-8 max-w-md mx-auto">
              Add an extra layer of security to your account. You'll need an authenticator 
              app like Google Authenticator or Authy.
            </p>
            
            <div className="space-y-4 text-left max-w-md mx-auto mb-8">
              {[
                'Download an authenticator app if you don\'t have one',
                'Scan the QR code with your authenticator app',
                'Enter the verification code to complete setup',
                'Save your backup codes in a secure location',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#2BBBAD]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-[#2BBBAD]">{i + 1}</span>
                  </div>
                  <span className="text-sm text-[#8892B0]">{text}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={initiateSetup}
              disabled={loading}
              className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold px-8 py-4"
              data-testid="start-mfa-setup-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Get Started'}
            </Button>
          </div>
        )}

        {/* Step 2: Scan QR Code */}
        {step === 2 && setupData && (
          <div className="glass-card rounded-2xl p-8 animate-fade-in">
            <h1 className="text-2xl font-bold text-[#E6F1FF] mb-2 text-center">
              Scan QR Code
            </h1>
            <p className="text-[#8892B0] mb-8 text-center">
              Open your authenticator app and scan this QR code
            </p>

            <div className="flex justify-center mb-6">
              <div className="qr-code-container">
                <QRCode
                  value={setupData.provisioning_uri}
                  size={200}
                  level="M"
                />
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#0B1C2D] border border-[#2BBBAD]/20 mb-6">
              <p className="text-xs text-[#8892B0] mb-2">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm text-[#64FFDA] font-mono break-all">
                  {setupData.secret_key}
                </code>
                <button
                  onClick={copySecret}
                  className="p-2 rounded-lg hover:bg-[#2BBBAD]/20 transition-colors"
                >
                  {copiedSecret ? (
                    <Check size={18} className="text-[#10B981]" />
                  ) : (
                    <Copy size={18} className="text-[#8892B0]" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border-[#64FFDA]/20 text-[#8892B0]"
              >
                <ChevronLeft size={18} className="mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="flex-1 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D]"
                data-testid="continue-to-verify-button"
              >
                Continue
                <ChevronRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Verify */}
        {step === 3 && (
          <div className="glass-card rounded-2xl p-8 animate-fade-in">
            <h1 className="text-2xl font-bold text-[#E6F1FF] mb-2 text-center">
              Verify Setup
            </h1>
            <p className="text-[#8892B0] mb-8 text-center">
              Enter the 6-digit code from your authenticator app
            </p>

            <div className="flex justify-center mb-8">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={setVerificationCode}
                data-testid="mfa-verify-input"
              >
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className="w-12 h-14 text-xl font-bold bg-[#0B1C2D] border-[#2BBBAD]/30 text-[#E6F1FF] rounded-lg"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 border-[#64FFDA]/20 text-[#8892B0]"
              >
                <ChevronLeft size={18} className="mr-2" />
                Back
              </Button>
              <Button
                onClick={verifySetup}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D]"
                data-testid="verify-mfa-button"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Backup Codes */}
        {step === 4 && (
          <div className="glass-card rounded-2xl p-8 animate-fade-in">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#10B981]/20 flex items-center justify-center">
              <Check className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="text-2xl font-bold text-[#E6F1FF] mb-2 text-center">
              MFA Enabled Successfully!
            </h1>
            <p className="text-[#8892B0] mb-6 text-center">
              Save these backup codes. You can use them if you lose access to your authenticator.
            </p>

            <div className="p-4 rounded-xl bg-[#0B1C2D] border border-[#FFCC00]/30 mb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-[#FFCC00]">Backup Codes</p>
                <button
                  onClick={copyBackupCodes}
                  className="flex items-center gap-1 text-xs text-[#8892B0] hover:text-[#E6F1FF]"
                >
                  {copiedBackup ? (
                    <>
                      <Check size={14} className="text-[#10B981]" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copy all
                    </>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <code key={i} className="text-sm text-[#E6F1FF] font-mono p-2 rounded bg-[#1A2F4B]/50">
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/20 mb-6">
              <p className="text-xs text-[#FF3B30]">
                <strong>Important:</strong> These codes will only be shown once. Store them in a secure location.
              </p>
            </div>

            <Button
              onClick={() => navigate('/settings')}
              className="w-full bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold"
              data-testid="finish-mfa-setup-button"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
